<template>
  <div
    class="nav-menu foreigner_navigation"
    :class="[
      isExpanded ? 'nav-menu__collapsed' : ''
    ]"
    @mouseenter="isTrackingMouseEnter ? isExpanded = true : undefined"
    @mouseleave="isExpanded = false"
  >
    <div>
      <div
        class="tw-bg-primary tw-pt-[7px] tw-px-[2px] tw-pb-[5px]"
        @click="isExpanded = !isExpanded"
      >
        <icon-logo />
      </div>
      <div>
        <AppNavSubmenu
          v-for="(familyMember) in familyOverview"
          :key="familyMember.id"
          :force-collapse="!isExpanded"
        >
          <template #icon>
            <i-user-avatar
              :avatar="familyMember.avatar_url && familyMember.avatar_url['64'] && `${$baseHost}/${familyMember.avatar_url['64']}`"
              :fullname="familyMember.name"
              :status="familyMember.isShowBadge ? 1000 : null"
              size="mini"
              class="tw-w-[40px] tw-h-[40px] tw-cursor-pointer tw-ml-1"
              @click.stop="navigateToForeignerPage(familyMember.id)"
            />
          </template>

          <template #title>
            <div
              class="tw-font-medium tw-block tw-absolute tw-left-14 tw-top-1/2 -tw-translate-y-1/2 tw-cursor-pointer hover:tw-underline"
              @click.stop="navigateToForeignerPage(familyMember.id)"
            >
              {{ familyMember.name }}
            </div>
          </template>

          <template v-if="familyMember.permits.length || familyMember.questionnaires.length">
            <div
              v-if="familyMember.permits.length"
              class="tw-mb-4"
            >
              <h4 class="tw-text-gray-dark tw-text-xs tw-font-normal tw-mb-1">
                {{ $t('foreigners.tabs.permits') }}
              </h4>
              <ul class="tw-list-none tw-space-y-2 tw-pr-2">
                <li
                  v-for="(permit) in familyMember.permits"
                  :key="permit.id"
                  class="tw-text-sm tw-py-1 tw-flex tw-flex-col tw-cursor-pointer"
                  @click="navigateToPermitsPage(familyMember.id, permit.id)"
                >
                  <div class="tw-flex tw-items-center tw-justify-between">
                    {{ permit.permit_type_name }}
                    <div @click.stop="navigateToPermitDocumentsPage(familyMember.id, permit.id)">
                      <el-badge
                        :value="permit.permit_document_missing_count"
                        class="item"
                        :hidden="permit.permit_document_missing_count === 0"
                        :max="9"
                      >
                        <icon-document-upload />
                      </el-badge>
                    </div>
                  </div>
                  <i-step-progress
                    :steps="permit.status_count"
                    :active-step="permit.current_status_human_order"
                    class="tw-mt-4"
                  />
                </li>
              </ul>
            </div>

            <div v-if="familyMember.questionnaires.length">
              <h4 class="tw-text-gray-dark tw-text-xs tw-font-normal tw-mb-1">
                {{ $t('foreigners.tabs.questionnaires') }}
              </h4>
              <ul class="tw-list-none tw-space-y-1">
                <li
                  v-for="(questionnaire) in familyMember.questionnaires"
                  :key="questionnaire.id"
                  class="tw-text-sm tw-py-1 tw-cursor-pointer hover:tw-underline"
                  @click="navigateToQuestionnairesPage(questionnaire.id)"
                >
                  {{ questionnaire.survey_type_name }}
                </li>
              </ul>
            </div>
          </template>
        </AppNavSubmenu>
      </div>
    </div>
    <div>
      <NavItemV2
        to="/me"
        :label="$t('main.navigation.my_account')"
      >
        <template #icon>
          <icon-settings />
        </template>
      </NavItemV2>
      <NavItemV2
        to="/faq"
        :label="$t('main.navigation.faq')"
        @click.stop="logout"
      >
        <template #icon>
          <icon-faq />
        </template>
      </NavItemV2>
      <NavItemV2
        :label="$t('main.navigation.logout')"
        @click="logout"
      >
        <template #icon>
          <icon-logout />
        </template>
      </NavItemV2>
    </div>
  </div>
</template>

<script>
import NavItemV2 from '@/components/Navigation/NavItemV2.vue';
import { authComputed } from '@/vuex/helpers';
import { mapState } from 'vuex';
import AppNavSubmenu from '@/components/AppNavSubmenu.vue';

export default {
  name: 'ForeignerAppNavV2',
  components: { AppNavSubmenu, NavItemV2 },
  data() {
    return {
      isExpanded: false,
      openAccordions: [],
      wrapperHeights: [],
      isTrackingMouseEnter: true,
    };
  },
  computed: {
    ...authComputed,
    ...mapState({
      familyOverview: (state) => state.v2.foreigner.family.permitsAndQuestionnairesByForeigner,
      me: (state) => state.v2.shared.profile.me,
    }),
  },
  mounted() {
    this.getWholeFamilyOverview();
    this.$nextTick(() => {
      this.wrapperHeights = this.familyOverview.map((_, index) => {
        const wrapper = this.$refs.wrapper?.[index];
        const content = wrapper && wrapper.querySelector('[ref="content"]');
        return content ? content.scrollHeight : 0;
      });
    });
  },
  methods: {
    logout() {
      clearInterval(this.$refreshNotificationsInterval);
      this.$store.dispatch('auth/logout');
    },
    getWholeFamilyOverview() {
      this.$store.dispatch('v2/foreigner/family/loadOverview', { family: this.me.family });
    },
    navigateToForeignerPage(fid) {
      const isOwnDashboard = this.me.id === fid && this.$route.name !== 'dashboard';
      const isForeignerDashboard = this.me.id !== fid &&
        (this.$route.name !== 'dashboardForeigner' || this.$route.params.fid !== fid);

      if (isOwnDashboard) {
        this.$router.push({ name: 'dashboard' });
      } else if (isForeignerDashboard) {
        this.$router.push({ name: 'dashboardForeigner', params: { fid } });
      }

      this.afterNavigateCallback();
    },
    navigateToPermitsPage(fid, pid) {
      if (this.$route.name !== 'permitDetail'
        || this.$route.params.pid !== pid
        || this.$route.params.fid !== fid
        || this.$route.query.fid !== fid
      ) {
        this.$router.push({
          name: 'permitDetail',
          params: { fid, pid },
          query: { fid },
        });
      }
      this.afterNavigateCallback();
    },
    navigateToPermitDocumentsPage(fid, pid) {
      if (
        this.$route.name !== 'permitDetailDocuments'
        || this.$route.params.fid !== fid
        || this.$route.params.pid !== pid
        || this.$route.query.fid !== fid
      ) {
        this.$router.push({
          name: 'permitDetailDocuments',
          params: { fid, pid },
          query: { fid },
        });
      }

      this.afterNavigateCallback();
    },
    navigateToQuestionnairesPage(qid) {
      const url = this.$router.resolve({
        name: 'ForeignerForeignersQuestionnaire',
        params: { qid },
      }).href;

      window.open(url, '_blank');
      this.afterNavigateCallback();
    },
    afterNavigateCallback() {
      this.isExpanded = false;
    },
  },
};
</script>
